"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type MultiSelectOption = { value: string; label: string };

type MultiSelectProps = {
	options: MultiSelectOption[];
	values: string[];
	onChange: (nextValues: string[]) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	emptyMessage?: string;
	className?: string;
	disabled?: boolean;
	maxVisibleItems?: number;
};

export function MultiSelect({
	options,
	values,
	onChange,
	placeholder = "Auswählen...",
	searchPlaceholder = "Suchen...",
	emptyMessage = "Keine Optionen gefunden",
	className,
	disabled = false,
	maxVisibleItems = 2,
}: MultiSelectProps) {
	const [open, setOpen] = React.useState(false);

	const toggleValue = (value: string) => {
		if (values.includes(value)) {
			onChange(values.filter((v) => v !== value));
		} else {
			onChange([...values, value]);
		}
	};

	const selectedLabels = React.useMemo(() => {
		const map = new Map(options.map((o) => [o.value, o.label] as const));
		return values.map((v) => map.get(v) ?? v);
	}, [options, values]);

	const buttonLabel = React.useMemo(() => {
		if (selectedLabels.length === 0) return placeholder;
		if (selectedLabels.length <= maxVisibleItems)
			return selectedLabels.join(", ");
		const shown = selectedLabels.slice(0, maxVisibleItems).join(", ");
		return `${shown} +${selectedLabels.length - maxVisibleItems}`;
	}, [selectedLabels, placeholder, maxVisibleItems]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					aria-expanded={open}
					className={cn("w-full justify-between", className)}
					disabled={disabled}
				>
					<span className="truncate text-left">{buttonLabel}</span>
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[--radix-popover-trigger-width] p-0"
				align="start"
				style={{ zIndex: 150 }}
				onWheel={(e) => e.stopPropagation()}
				onTouchMove={(e) => e.stopPropagation()}
			>
				<Command>
					<CommandInput placeholder={searchPlaceholder} className="h-9" />
					<CommandList className="max-h-[300px] overflow-auto">
						<CommandEmpty>{emptyMessage}</CommandEmpty>
						<CommandGroup>
							{options.map((option) => {
								const checked = values.includes(option.value);
								return (
									<CommandItem
										key={option.value}
										value={`${option.label} ${option.value}`}
										onSelect={() => toggleValue(option.value)}
									>
										{option.label}
										<Check
											className={cn(
												"ml-auto h-4 w-4",
												checked ? "opacity-100" : "opacity-0",
											)}
										/>
									</CommandItem>
								);
							})}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}

export default MultiSelect;
